<template>
  <div class="sos-wrapper">
    <button
      class="action-card-btn action-card-btn--sos"
      @click="openSosModal"
      :disabled="isBusy"
      aria-label="Báo cáo sự cố"
    >
      <i class="fa fa-exclamation-triangle"></i>
      <span class="label">SOS</span>
    </button>

    <div
      v-if="showSosModal && !cameraOpen"
      class="modal fade show d-block modal-overlay"
      tabindex="-1"
    >
      <div class="modal-dialog modal-lg modal-full-mobile" role="document">
        <div class="modal-content">
          <form @submit.prevent="submitSosForm">
            <div class="modal-header bg-danger text-white">
              <h5 class="modal-title font-weight-bold">
                <i class="fa fa-exclamation-triangle mr-2"></i>
                Báo cáo Sự cố Khẩn cấp
              </h5>
              <button
                type="button"
                class="close text-white"
                @click="closeSosModal"
                :disabled="isBusy"
              >
                <span>&times;</span>
              </button>
            </div>

            <div class="modal-body">
              <div v-if="formError" class="alert alert-danger py-2">
                {{ formError }}
              </div>

              <div class="form-group">
                <label class="font-weight-bold">
                  Mô tả sự cố (Bắt buộc) <span class="text-danger">*</span>
                </label>
                <textarea
                  v-model.trim="form.noiDungSuCo"
                  class="form-control form-control-lg"
                  rows="3"
                  required
                  placeholder="Ví dụ: Xe nổ lốp, Động cơ có khói..."
                ></textarea>
              </div>

              <div class="form-row">
                <div class="form-group col-12 col-md-6">
                  <label class="font-weight-bold">ODO hiện tại (Km)</label>
                  <input
                    v-model.number="form.odoHienTai"
                    type="number"
                    inputmode="numeric"
                    class="form-control form-control-lg"
                    min="0"
                    placeholder="Nhập số km..."
                  />
                </div>

                <div class="form-group col-12 col-md-6">
                  <label class="font-weight-bold">
                    Hình ảnh hiện trường
                    <small class="text-muted font-weight-normal"
                      >({{ form.anhSuCoUrls.length }} ảnh)</small
                    >
                  </label>

                  <div class="image-grid mb-2" v-if="form.anhSuCoUrls.length > 0">
                    <div
                      class="image-item"
                      v-for="(url, index) in form.anhSuCoUrls"
                      :key="index"
                    >
                      <img :src="url" alt="Ảnh sự cố" @click="viewImage(url)" />
                      <button
                        type="button"
                        class="btn-remove-img"
                        @click="removeImage(index)"
                        :disabled="isBusy"
                      >
                        &times;
                      </button>
                    </div>
                  </div>

                  <button
                    type="button"
                    class="btn btn-outline-danger btn-block btn-dashed"
                    :disabled="isBusy"
                    @click="openCamera"
                  >
                    <span
                      v-if="uploadingImage"
                      class="spinner-border spinner-border-sm mr-1"
                    ></span>
                    <i v-else class="fa fa-camera mr-1"></i>
                    {{
                      form.anhSuCoUrls.length > 0
                        ? "Chụp thêm ảnh"
                        : "Chụp ảnh sự cố"
                    }}
                  </button>

                  <div v-if="uploadingImage" class="text-primary small mt-1 text-center">
                    Đang xử lý ảnh...
                  </div>
                </div>
              </div>
            </div>

            <div class="modal-footer sticky-footer-mobile">
              <button
                type="button"
                class="btn btn-outline-secondary"
                @click="closeSosModal"
                :disabled="isBusy"
              >
                Hủy
              </button>
              <button
                type="submit"
                class="btn btn-danger font-weight-bold px-4"
                :disabled="isBusy"
              >
                <span
                  v-if="isBusy"
                  class="spinner-border spinner-border-sm mr-1"
                ></span>
                <i v-else class="fa fa-paper-plane mr-1"></i>
                Gửi Báo Cáo
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <FullscreenCamera
      v-model="cameraOpen"
      title="Chụp ảnh sự cố"
      :busy="isBusy"
      initial-facing="environment"
      @confirm="onCameraConfirm"
      @cancel="onCameraCancel"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from "pinia";
import { useBaoDuongXeStore } from "@/stores/baoDuongXeStore";
import fileService from "@/services/fileService";
import FullscreenCamera from "@/components/FullscreenCamera.vue";

// UPDATE: Dùng mảng anhSuCoUrls thay vì string
const DEFAULT_SOS_FORM = {
  noiDungSuCo: "",
  anhSuCoUrls: [], 
  odoHienTai: null,
};

const SOS_FOLDER = "sos";

export default {
  name: "SosComponent",
  components: {
    FullscreenCamera,
  },
  data() {
    return {
      showSosModal: false,
      form: { ...DEFAULT_SOS_FORM }, // Clone object để tránh tham chiếu
      
      // Fix lỗi reference: Đảm bảo anhSuCoUrls luôn là mảng
      // (Phòng trường hợp reset form bị lỗi)
      
      formError: null,
      cameraOpen: false,
      uploadingImage: false,
    };
  },
  computed: {
    ...mapState(useBaoDuongXeStore, ["isLoadingDetail", "detailError"]),
    isBusy() {
      return this.isLoadingDetail || this.uploadingImage;
    },
  },
  methods: {
    ...mapActions(useBaoDuongXeStore, [
      "createTicketFromSos",
      "clearTicketDetail",
    ]),

    openSosModal() {
      // Deep clone để reset sạch sẽ form
      this.form = JSON.parse(JSON.stringify(DEFAULT_SOS_FORM));
      this.formError = null;
      this.clearTicketDetail();
      this.showSosModal = true;
      document.body.style.overflow = "hidden";
    },

    closeSosModal() {
      this.showSosModal = false;
      document.body.style.overflow = "";
    },

    openCamera() {
      this.cameraOpen = true;
    },

    onCameraCancel() {
      this.cameraOpen = false;
    },

    async onCameraConfirm({ blob }) {
      if (!blob || this.uploadingImage) return;
      this.uploadingImage = true;
      this.formError = null;

      try {
        const rawFile = new File([blob], `sos_${Date.now()}.jpg`, {
          type: "image/jpeg",
        });

        const formData = new FormData();
        formData.append("files", rawFile, rawFile.name);
        formData.append("folderName", SOS_FOLDER);

        const res = await fileService.upload(formData);
        const first = Array.isArray(res.data) ? res.data[0] : null;

        if (first?.location) {
          // UPDATE: Push vào mảng thay vì gán đè
          this.form.anhSuCoUrls.push(first.location);
        } else {
          throw new Error("Server không trả về URL file.");
        }
      } catch (e) {
        console.error("Lỗi upload ảnh SOS:", e);
        this.formError =
          e?.response?.data?.error ||
          e?.message ||
          "Không upload được ảnh. Vui lòng thử lại.";
      } finally {
        this.uploadingImage = false;
        this.cameraOpen = false;
      }
    },

    removeImage(index) {
      this.form.anhSuCoUrls.splice(index, 1);
    },

    viewImage(url) {
      window.open(url, "_blank");
    },

    async submitSosForm() {
      this.formError = null;
      if (!this.form.noiDungSuCo) {
        this.formError = "Vui lòng mô tả sự cố.";
        return;
      }

      try {
        // UPDATE: Payload gửi mảng anhSuCoUrls
        const payload = {
          noiDungSuCo: this.form.noiDungSuCo,
          anhSuCoUrls: this.form.anhSuCoUrls, // Gửi list
          odoHienTai: this.form.odoHienTai || null,
        };
        
        const newTicket = await this.createTicketFromSos(payload);
        this.closeSosModal();
        
        // Show alert hoặc toast đẹp hơn nếu có
        alert(`Đã gửi SOS thành công! Mã phiếu: #${newTicket.id}`);
      } catch (e) {
        this.formError = this.detailError || "Lỗi gửi báo cáo. Vui lòng thử lại.";
      }
    },
  },
};
</script>

<style scoped>
.sos-wrapper {
  padding: 0;
}

/* --- BUTTON SOS STYLE (Giữ nguyên) --- */
.action-card-btn {
  width: 100%;
  height: 100%;
  min-height: 92px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0.75rem;
  font-size: 1.1rem;
  font-weight: 500;
  text-align: center;
  cursor: pointer;
  border: 1px solid transparent;
  border-radius: 0.75rem;
  transition: all 0.15s ease-in-out;
  text-transform: uppercase;
  color: #fff;
  gap: 0.5rem;
}
.action-card-btn i {
  font-size: 20px;
  line-height: 1;
}
.action-card-btn .label {
  font-size: 16px;
}
.action-card-btn--sos {
  background: #dc3545;
  box-shadow: 0 4px 6px rgba(220, 53, 69, 0.3);
}
.action-card-btn--sos:active {
  transform: scale(0.98);
}

/* --- IMAGE GRID STYLE (MỚI) --- */
.image-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.image-item {
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #dee2e6;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.image-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: pointer;
}

.btn-remove-img {
  position: absolute;
  top: 0;
  right: 0;
  background: rgba(255, 0, 0, 0.8);
  color: white;
  border: none;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  cursor: pointer;
  border-bottom-left-radius: 6px;
}

.btn-dashed {
  border-style: dashed;
  border-width: 2px;
}

/* --- MODAL STYLE --- */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  backdrop-filter: blur(2px);
}

.modal-full-mobile {
  width: 100%;
  margin: 1rem auto;
  max-width: 500px;
}

@media (max-width: 576px) {
  .modal-full-mobile {
    margin: 0;
    max-width: 100%;
    height: 100%;
  }

  .modal-full-mobile .modal-content {
    height: 100dvh;
    border-radius: 0;
    display: flex;
    flex-direction: column;
  }

  .modal-full-mobile .modal-body {
    flex-grow: 1;
    overflow-y: auto;
  }
}

.sticky-footer-mobile {
  flex-shrink: 0;
  background: #fff;
  z-index: 2;
  border-top: 1px solid #e9ecef;
  padding: 0.75rem;
}
</style>